import { Resources } from 'constants/resources';

import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useCreate, useNotify, useRecordContext } from 'react-admin';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Button, CancelButton } from 'shared/mui/Buttons';
import { Card, CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { DEFAULT_TIMEZONE } from 'shared/mui/DatePicker/config/defaultValues';
import { TimezoneType } from 'shared/mui/DatePicker/constants/timezones';
import {
  DateRangePickerValue,
  DateTimeRangePicker,
} from 'shared/mui/DatePicker/DateTimeRangePicker/DateTimeRangePicker';
import { Dialog, DialogProps } from 'shared/mui/Dialogs';
import { TimezoneSelectField } from 'shared/mui/Fields/TimezoneSelectField/TimezoneSelectField';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Typography } from 'shared/mui/Typography';
import { calculateDateRange } from 'shared/widgets/CommissionsTabs/CommissionsTabsTask/utils/calculateDateRange';
import { useSyncNestedDialogState } from 'shared/widgets/CommissionsTabs/context/CommissionsNestedDialogsContext';
import { CommissionRecalculationTask } from 'types/embedded/commissionRecalculationTask';

import { FormItemStyled } from './CommissionsTabsTaskCreateDialog.styled';

type Props = {
  onClose: VoidFunction;
  onSuccess: (data: CommissionRecalculationTask) => void;
} & DialogProps;

type FormValues = {
  appliedDate: [DateRangePickerValue, DateRangePickerValue];
  timezone: TimezoneType | null;
};

export const CommissionsTabsTaskCreateDialog: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const translate = useTypedTranslate();
  const [create, { isLoading }] = useCreate();
  const record = useRecordContext();
  const notify = useNotify();

  useSyncNestedDialogState(open);

  const form = useForm<FormValues>({
    defaultValues: {
      appliedDate: [null, null],
      timezone: null,
    },
    shouldUnregister: true,
  });

  const onSubmit = (values: FormValues) => {
    const [fromMs, toMs] = values?.appliedDate || [];
    const currentTz = values?.timezone?.name || DEFAULT_TIMEZONE;

    const { recalculateFrom, recalculateTo } = calculateDateRange({
      fromMs: fromMs || 0,
      toMs: toMs || 0,
      tzName: currentTz,
    });

    create(
      Resources.CommissionRecalculationTasks,
      {
        data: {
          commission: record?.id,
          recalculateFrom,
          recalculateTo,
          timeZone: currentTz,
        },
      },
      {
        onSuccess,
        onError: (error: any) => {
          notify(
            `${translate('app.commissionsRecalculation.createDialog.error')}: ${error.message}`,
            { type: 'error' },
          );
        },
      },
    );
  };

  return (
    <Dialog onClose={onClose} open={open} width="640px">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card>
            <CardHeader
              appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
              title={translate('app.commissionsRecalculation.createDialog.title')}
              titleSize="smallBold"
            />
            <CardContent>
              <Typography>
                {translate('app.commissionsRecalculation.createDialog.message')}
              </Typography>
              <FormItemStyled>
                <TimezoneSelectField control={form.control} name="timezone" />
              </FormItemStyled>
              <FormItemStyled>
                <Controller
                  control={form.control}
                  name="appliedDate"
                  render={({
                    field: { onChange, onBlur, value },
                    fieldState,
                    formState: { errors },
                  }) => (
                    <DateTimeRangePicker
                      error={!!errors.appliedDate}
                      helperText={fieldState?.error?.message}
                      label={translate('app.commissionsRecalculation.createDialog.date')}
                      maxDate={new Date()}
                      onBlur={onBlur}
                      onChange={onChange}
                      required
                      value={value}
                    />
                  )}
                  rules={{
                    validate: {
                      required: (value) =>
                        (!!value[0] && !!value[1]) || translate('app.validation.required'),
                    },
                  }}
                />
              </FormItemStyled>
            </CardContent>
            <CardActionsButtons
              rightActionsSlot={[
                <CancelButton key="cancel-button" onClick={onClose} variant="outlined" />,
                <Button
                  color="primary"
                  isLoading={isLoading}
                  key="save-button"
                  label={translate('app.buttons.create')}
                  type="submit"
                  variant="contained"
                />,
              ]}
            />
          </Card>
        </form>
      </FormProvider>
    </Dialog>
  );
};
